import React, { useState, useMemo } from "react";
import { createDateInBrazil, getTodayInBrazil } from "../../../utils/dateUtils";

interface HorizontalDatePickerProps {
  selectedDate: string;
  onDateChange: (date: string) => void;
  className?: string;
  showBorder?: boolean;
  disablePast?: boolean;
}

export const HorizontalDatePicker: React.FC<HorizontalDatePickerProps> = ({
  selectedDate,
  onDateChange,
  className = "",
  showBorder = true,
  disablePast = true,
}) => {
  const today = getTodayInBrazil();
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    // Get the start of the week for the selected date or today
    const baseDate = selectedDate || today;
    const date = createDateInBrazil(baseDate);
    const dayOfWeek = date.getDay();
    const sundayOffset = -dayOfWeek;
    date.setDate(date.getDate() + sundayOffset);
    return date.toISOString().split('T')[0];
  });

  const weekDays = useMemo(() => {
    const days = [];
    const startDate = createDateInBrazil(currentWeekStart);

    for (let i = 0; i < 7; i++) {
      const date = createDateInBrazil(startDate.toISOString().split('T')[0]);
      date.setDate(startDate.getDate() + i);

      const dateString = date.toISOString().split('T')[0];
      const dayName = date.toLocaleDateString("pt-BR", {
        weekday: "short",
        timeZone: "America/Sao_Paulo",
      });
      const dayNumber = date.toLocaleDateString("pt-BR", {
        day: "numeric",
        timeZone: "America/Sao_Paulo",
      });
      const isToday = dateString === today;
      const isSelected = dateString === selectedDate;
      const isPast = dateString < today;

      days.push({
        dateString,
        dayName: dayName.charAt(0).toUpperCase() + dayName.slice(1),
        dayNumber: parseInt(dayNumber),
        isToday,
        isSelected,
        isPast,
      });
    }

    return days;
  }, [currentWeekStart, today, selectedDate]);

  const navigateWeek = (direction: "prev" | "next") => {
    const startDate = createDateInBrazil(currentWeekStart);
    const offset = direction === "prev" ? -7 : 7;
    startDate.setDate(startDate.getDate() + offset);
    setCurrentWeekStart(startDate.toISOString().split('T')[0]);
  };

  const handleDateSelect = (dateString: string, isPast: boolean) => {
    if (!disablePast || !isPast) {
      onDateChange(dateString);
    }
  };

  return (
    <div
      className={`rounded-2xl ${
        showBorder
          ? "bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 p-6 shadow-soft "
          : ""
      }${className}`}
    >
      {/* Header with navigation */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigateWeek("prev")}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors duration-200"
        >
          <span className="text-gray-600 dark:text-slate-300 text-lg">←</span>
        </button>

        <div className="text-center">
          <h4 className="font-semibold text-gray-800 dark:text-slate-100">
            {new Date(currentWeekStart + "T00:00:00").toLocaleDateString(
              "pt-BR",
              {
                month: "long",
                year: "numeric",
                timeZone: "America/Sao_Paulo",
              }
            ).replace(/^\w/, (c) => c.toUpperCase())}
          </h4>
        </div>

        <button
          onClick={() => navigateWeek("next")}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors duration-200"
        >
          <span className="text-gray-600 dark:text-slate-300 text-lg">→</span>
        </button>
      </div>

      {/* Week days grid */}
      <div className="grid grid-cols-7 gap-2">
        {weekDays.map((day) => (
          <button
            key={day.dateString}
            type="button"
            onClick={() => handleDateSelect(day.dateString, day.isPast)}
            disabled={disablePast && day.isPast}
            className={`
              flex flex-col items-center justify-center p-3 rounded-xl transition-all duration-200
              ${
                disablePast && day.isPast
                  ? "bg-transparent text-gray-300 dark:text-slate-600 cursor-not-allowed"
                  : day.isSelected
                  ? "bg-gradient-to-r from-sky-500 to-sky-600 text-white shadow-medium transform scale-105"
                  : day.isToday
                  ? "bg-sky-50 dark:bg-sky-950 text-sky-600 dark:text-sky-300 border-2 border-sky-200 dark:border-sky-800 hover:bg-sky-100 dark:hover:bg-sky-900 hover:scale-105"
                  : "bg-gray-50 dark:bg-slate-700/80 text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-600 hover:scale-105"
              }
            `}
          >
            <span className="text-xl font-bold mb-1">{day.dayNumber}</span>
            <span className="text-xs font-medium">{day.dayName}</span>
            {day.isToday && (
              <div className="w-1 h-1 bg-sky-500 rounded-full mt-1"></div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};
